<template>
  <ModalPanel :open="open" :initial-focus="closeButton" @close="emit('close')">
    <DialogTitle as="h3" class="text-base font-semibold text-gray-900 dark:text-white">
      Install the agent on {{ runnerName }}
    </DialogTitle>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      Run this on the runner server. It installs the Manage Runners agent as a systemd service and
      needs root, systemd and Python 3.8 or later. Running it again reinstalls or updates the agent.
    </p>

    <p
      v-if="loading"
      class="mt-4 text-sm text-gray-500 dark:text-gray-400"
      data-testid="install-loading"
    >
      Creating install command…
    </p>
    <p v-else-if="error" class="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
      {{ error }}
    </p>
    <template v-else-if="link">
      <div class="mt-4">
        <div class="flex items-center justify-between gap-2">
          <span
            class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >Install command</span
          >
          <button
            type="button"
            class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 dark:text-indigo-400 dark:hover:bg-white/5 cursor-pointer"
            :disabled="expired"
            data-testid="install-copy"
            @click="copy"
          >
            <ClipboardDocumentIcon class="size-4" aria-hidden="true" />
            {{ copied ? 'Copied' : 'Copy' }}
          </button>
        </div>
        <pre
          class="mt-1 overflow-x-auto rounded-md bg-gray-900 p-3 text-xs text-gray-100 select-all whitespace-pre-wrap break-all"
          :class="{ 'opacity-40 line-through': expired }"
          data-testid="install-command"
          >{{ link.command }}</pre>
        <p v-if="copyFailed" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          Copying failed. Select the command and copy it manually.
        </p>
        <p
          class="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
          data-testid="install-directory"
        >
          Run it in a directory that no other user or CI job can write to, such as your home
          directory. There, the downloaded script, which holds the server's credential, is readable
          only by you.
        </p>
        <p class="mt-2 text-xs text-gray-500 dark:text-gray-400" data-testid="install-expiry">
          <template v-if="expired">This command has expired. Create a new one.</template>
          <template v-else>
            Works once, until {{ expiryTime }} ({{ minutesLeft }}
            {{ minutesLeft === 1 ? 'minute' : 'minutes' }} left). It carries the server's
            credential, so don't share it; running it replaces the previous installation's
            credential.
          </template>
        </p>
      </div>
      <div class="mt-4">
        <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >Uninstall later</span
        >
        <pre
          class="mt-1 overflow-x-auto rounded-md bg-gray-100 p-3 text-xs text-gray-800 dark:bg-white/5 dark:text-gray-200"
          data-testid="uninstall-command"
          >{{ link.uninstallCommand }}</pre>
      </div>
    </template>

    <div class="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
      <button
        ref="closeButton"
        type="button"
        class="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20 cursor-pointer"
        @click="emit('close')"
      >
        Close
      </button>
      <button
        type="button"
        :disabled="loading"
        class="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
        data-testid="install-new"
        @click="create"
      >
        New install command
      </button>
    </div>
  </ModalPanel>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { DialogTitle } from '@headlessui/vue';
import { ClipboardDocumentIcon } from '@heroicons/vue/20/solid';
import ModalPanel from '@/components/ModalPanel.vue';
import { createInstallLink, type InstallLink } from '@/api/external-runners';
import { runnerProblem } from '@/api/runners';

const props = defineProps<{
  open: boolean;
  runnerId: string;
  runnerName: string;
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

const closeButton = ref<HTMLElement | null>(null);
const link = ref<InstallLink | null>(null);
const loading = ref(false);
const error = ref('');
const copied = ref(false);
const copyFailed = ref(false);
const now = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

const expiresAt = computed(() => (link.value ? Date.parse(link.value.expiresAt) : 0));
const expired = computed(() => !!link.value && now.value >= expiresAt.value);
const minutesLeft = computed(() => Math.max(1, Math.ceil((expiresAt.value - now.value) / 60_000)));
const expiryTime = computed(() =>
  new Date(expiresAt.value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
);

async function create() {
  loading.value = true;
  error.value = '';
  copied.value = false;
  copyFailed.value = false;
  try {
    link.value = await createInstallLink(props.runnerId);
    now.value = Date.now();
  } catch (e) {
    link.value = null;
    error.value = runnerProblem(e, 'Creating the install command failed.').message;
  } finally {
    loading.value = false;
  }
}

async function copy() {
  if (!link.value) return;
  try {
    await navigator.clipboard.writeText(link.value.command);
    copied.value = true;
    copyFailed.value = false;
    setTimeout(() => (copied.value = false), 2000);
  } catch {
    copyFailed.value = true;
  }
}

watch(
  () => props.open,
  (open) => {
    if (timer) clearInterval(timer);
    timer = null;
    if (!open) {
      // The command holds a credential; it is not kept once the dialog is closed.
      link.value = null;
      return;
    }
    timer = setInterval(() => (now.value = Date.now()), 15_000);
    void create();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>
